import { analytics, EEVENT_LABEL } from 'affiny-core/analytics';
import { useIsSub } from 'affiny-core/hooks';
import { EActionCode } from 'affiny-core/modules/api/repositories/payment/paymentActionCodes';
import { authActions } from 'affiny-core/modules/auth/actions';
import { countersActions } from 'affiny-core/modules/counters/actions';
import { goToRateCardActions } from 'affiny-core/redux/actions/goToRatecardActions';
import { Container } from 'affiny-desktop/src/components/desktop/Container';
import { Box, Flex, rem } from 'affiny-ui/src';
import React, {
  SyntheticEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { useOnClickOutside } from '../../../hooks';
import { DropDownMenu } from '../DropDownMenu';
import { Logo } from '../Logo';
import { HeaderNavList } from './HeaderNavList';
import { HeaderProfile } from './HeaderProfile';

export const HEADER_HEIGHT = rem(82);

export const Header = () => {
  const [displayMenu, setDisplayMenu] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const isSub = useIsSub();

  useOnClickOutside(ref, () => setDisplayMenu(false));

  useEffect(() => {
    dispatch(countersActions.getCounters());
  }, [dispatch]);

  const handleLogoClick = useCallback(() => {
    analytics.trackEvent(EEVENT_LABEL.AFFINY_LOGO_CLICK);
  }, []);

  const handleProfileClick = useCallback(() => {
    analytics.trackEvent(EEVENT_LABEL.NAV_MY_PROFILE_CLICK);

    setDisplayMenu(!displayMenu);
  }, [displayMenu]);

  const menuItems = useMemo(
    () => [
      {
        to: '/profile-edit',
        label: t('nav.menu.yourProfil'),
        location: 'profile-edit',
      },
      {
        to: '/my-subscription',
        label: t('nav.menu.subscription'),
        location: 'my-subscription',
        underlined: false,
        onClick: (e: SyntheticEvent) => {
          if (!isSub) {
            e.preventDefault();

            dispatch(
              goToRateCardActions.goToRateCard({
                actionCode: EActionCode.CTA_MY_SUBSCRIPTION,
              })
            );
          }
        },
      },
      {
        to: '/criteria',
        label: t('nav.menu.criteria'),
        location: 'criteria',
      },
      {
        to: '?target=personality-report',
        label: t('nav.menu.personalityReport'),
        location: 'personality-report',
      },
      {
        to: '/settings',
        label: t('nav.menu.yourParameters'),
        location: 'settings',
      },
      {
        to: '/',
        label: t('nav.menu.Logout'),
        location: 'logout',
        underlined: true,
        onClick: (e: SyntheticEvent) => {
          e.preventDefault();
          document.documentElement.style.display = 'none';
          dispatch(authActions.logout());

          return false;
        },
      },
    ],
    [t, isSub, dispatch]
  );

  return (
    <Box as="header" boxShadow="box" height={HEADER_HEIGHT} bg="light">
      <Container
        display="flex"
        alignItems="center"
        justifyContent="space-between"
      >
        <SLogoLink to="/home" onClick={handleLogoClick}>
          <Logo />
        </SLogoLink>
        <Flex>
          <HeaderNavList />
          <Flex flexGrow={0} ml="medium">
            <Flex ref={ref} onClick={handleProfileClick} cursor="pointer">
              <HeaderProfile />

              {displayMenu && (
                <Box
                  cursor="default"
                  position="absolute"
                  right="100%"
                  top="100%"
                  mt={rem(19)}
                  mr={rem(-61)}
                >
                  <DropDownMenu items={menuItems} />
                </Box>
              )}
            </Flex>
          </Flex>
        </Flex>
      </Container>
    </Box>
  );
};

const SLogoLink = styled(Link)`
  display: block;
`;
